// AutoLogout.js
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { handleLogout } from '@store/actions/auth';
import Swal from 'sweetalert2';
import { isUserLoggedIn } from '@utils';
import { sessionActivityKey } from '@src/auth/session';

const AutoLogout = () => {
  const dispatch = useDispatch();
  const timeoutId = useRef(null);
  const logoutInProgress = useRef(false);

  useEffect(() => {
    const limpiarTemporizador = () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
        timeoutId.current = null;
      }
    };

    const getInactivityTimeout = () => {
      let inactivityTimeout = 5 * 60 * 1000;
      const tiempoEsperaMinutos = localStorage.getItem('tiempoDeEsperaInactividad')

      if (tiempoEsperaMinutos) {
        const minutos = parseInt(tiempoEsperaMinutos, 10)
        if (!isNaN(minutos) && minutos > 0) {
          inactivityTimeout = minutos * 60 * 1000;
        }
      }

      return inactivityTimeout;
    };

    const cerrarSesionPorInactividad = () => {
      if (logoutInProgress.current || !isUserLoggedIn()) {
        return;
      }

      logoutInProgress.current = true;
      limpiarTemporizador();

      Swal.fire({
        title: 'Sesión cerrada por inactividad',
        text: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
        icon: 'info',
        showConfirmButton: false,
        timer: 5000,
        allowOutsideClick: false
      }).then(() => {
        localStorage.removeItem(sessionActivityKey);
        dispatch(handleLogout());
        logoutInProgress.current = false;
      });
    };

    const programarCierre = (delay = getInactivityTimeout()) => {
      limpiarTemporizador();

      if (!isUserLoggedIn()) {
        localStorage.removeItem(sessionActivityKey);
        return;
      }

      timeoutId.current = setTimeout(() => {
        cerrarSesionPorInactividad();
      }, Math.max(delay, 0));
    };

    const registrarActividad = () => {
      if (!isUserLoggedIn()) {
        limpiarTemporizador();
        localStorage.removeItem(sessionActivityKey);
        return;
      }

      localStorage.setItem(sessionActivityKey, String(Date.now()));
      programarCierre();
    };

    const validarSesionAlReanudar = () => {
      if (!isUserLoggedIn()) {
        limpiarTemporizador();
        localStorage.removeItem(sessionActivityKey);
        return;
      }

      const inactivityTimeout = getInactivityTimeout();
      const lastActivityAt = Number(localStorage.getItem(sessionActivityKey) || Date.now());
      const elapsed = Date.now() - lastActivityAt;

      if (elapsed >= inactivityTimeout) {
        cerrarSesionPorInactividad();
        return;
      }

      programarCierre(inactivityTimeout - elapsed);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        validarSesionAlReanudar();
      }
    };

    const eventosActividad = ['mousemove', 'keydown', 'scroll', 'click'];
    eventosActividad.forEach((event) => {
      window.addEventListener(event, registrarActividad);
    });
    window.addEventListener('focus', validarSesionAlReanudar);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    validarSesionAlReanudar();

    return () => {
      limpiarTemporizador();
      eventosActividad.forEach((event) => {
        window.removeEventListener(event, registrarActividad);
      });
      window.removeEventListener('focus', validarSesionAlReanudar);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dispatch]);

  return null; // No renderiza nada, ya que es un componente de inicialización
};

export default AutoLogout;
